/**
 * AutoGlow · Advisor · ScoreGauge.
 * Pure SVG arc · NO lib · color encodes tier (red/amber/green).
 */
import React from "react";

function tierColor(value, max = 100) {
  const pct = (value / max) * 100;
  if (pct >= 70) return "var(--ag-success, #16a34a)";
  if (pct >= 50) return "var(--ag-warning, #f59e0b)";
  if (pct >= 30) return "var(--ag-warning, #f59e0b)";
  return "var(--ag-danger, #ef4444)";
}

export function ScoreGauge({
  value,
  max = 100,
  label,
  size = 120,
  showValue = true,
  testId,
}) {
  if (value == null) {
    return (
      <div
        data-testid={testId || "score-gauge-empty"}
        className="flex h-32 items-center justify-center text-sm text-[var(--ag-text-dim)]"
      >
        Información insuficiente.
      </div>
    );
  }
  const v = Math.max(0, Math.min(Number(value), max));
  const color = tierColor(v, max);
  const radius = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = Math.PI * radius;  // half circle
  const offset = circumference * (1 - v / max);

  return (
    <div
      data-testid={testId || "score-gauge"}
      className="flex flex-col items-center"
    >
      <svg width={size} height={size / 1.5} viewBox={`0 0 ${size} ${size / 1.5}`} role="img" aria-label={`${label}: ${v}/${max}`}>
        {/* Background arc */}
        <path
          d={`M 12 ${cy} A ${radius} ${radius} 0 0 1 ${size - 12} ${cy}`}
          fill="none"
          stroke="var(--ag-border)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        {/* Value arc */}
        <path
          d={`M 12 ${cy} A ${radius} ${radius} 0 0 1 ${size - 12} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      {showValue && (
        <div className="-mt-6 text-center">
          <div className="text-3xl font-bold" data-testid={`${testId || "score-gauge"}-value`}>
            {v}
          </div>
          {label && (
            <div className="text-xs uppercase tracking-wide text-[var(--ag-text-dim)]">
              {label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PercentileGauge({ percentile, quartile, n, testId }) {
  if (percentile == null) {
    return (
      <div
        data-testid={testId || "percentile-gauge-empty"}
        className="rounded border border-dashed border-[var(--ag-border)] p-3 text-xs text-[var(--ag-text-dim)]"
      >
        Información insuficiente · sin datos suficientes para calcular percentil.
      </div>
    );
  }
  // Render a horizontal track with deciles + a needle at `percentile`
  return (
    <div data-testid={testId || "percentile-gauge"} className="w-full">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-2xl font-bold">p{percentile}</span>
        <span className="text-xs text-[var(--ag-text-dim)]">
          {quartile} {n != null ? `· N=${n}` : ""}
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-[var(--ag-surface-2)]">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-emerald-500/60"
          style={{ width: `${percentile}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-[var(--ag-text-dim)]">
        <span>p10</span><span>p25</span><span>p50</span><span>p75</span><span>p90</span>
      </div>
    </div>
  );
}
